import React from 'react';
import { styled, SxProp, View } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { useModal } from 'react-native-modalfy';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { IS_WEB } from '@utils';
import { Touchable } from '../common/Touchable';
import { Image } from '../common/Image';
import { BaseText } from '../common/BaseText';
import { Icon } from '../common/Icon';
import { Separator } from '../common/Separator';
import { HighlightText } from '../common/HighlightText';
import { createAspectRatioStyles } from '@styles';
import type { ISearchStore } from '@hooks';
import type { Course } from '@models/Courses/CourseModel';
import { CoverView } from '@components/views/CoverView';
import { modals } from '@modals';
import { MainStackParamListType, screens } from '@navigation';

interface ITouchableContainerStyleProps {
  width?: number;
}

const TouchableContainer = styled(Touchable)(
  (props: ITouchableContainerStyleProps) => ({
    flex: 1,
    width: props.width,
  }),
);

const CourseInfoContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
});

const CourseInfoText = styled(BaseText)({
  color: '$text100',
  variant: 'text.xsmall',
  ml: '$2',
});

const Title = styled(BaseText)({
  color: '$heading800',
  fontWeight: 'semi',
  variant: IS_WEB ? 'text.large' : 'text.medium',
});

const Description = styled(HighlightText)({
  color: '$text400',
  variant: IS_WEB ? 'text.medium' : 'text.small',
});

interface ICourseImageStyles {
  width?: number;
}

const CourseImage = styled(Image)((props: ICourseImageStyles) => ({
  ...createAspectRatioStyles(16 / 9, props.width ?? '100%'),
  borderRadius: 12,
  bg: '$background20',
}));

const coverViewStyles: SxProp = {
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 12,
  overflow: 'hidden',
};

const CourseImageCoverBackground = styled(View)({
  zIndex: -1,
  position: 'absolute',
  width: '100%',
  height: '100%',
  bg: '$heading800',
  opacity: 0.7,
});

export interface ICourseItemExtraStore extends ISearchStore {}

export interface ICourseItemProps {
  item: Course;
  width?: number;
  onPress(item: Course): void;
  extraStore?: ICourseItemExtraStore;
}

type NavigationPropType =
  NativeStackNavigationProp<MainStackParamListType>;

export const CourseItem = observer<ICourseItemProps>(
  ({ item, width, onPress, extraStore }) => {
    const { t } = useTranslation();
    const modal = useModal();
    const nav = useNavigation<NavigationPropType>();

    function navigateToSubscription() {
      nav.navigate(screens.ProfileNavigator, {
        screen: screens.Subscription,
      });
    }

    function onPressItem() {
      if (item.isAvailableForCurrentPlan) {
        onPress(item);
      } else {
        modal.openModal(modals.SubscribeSuggestionModal, {
          onPress: () => {
            navigateToSubscription();
            modal.closeModal();
          },
        });
      }
    }

    return (
      <TouchableContainer width={width} onPress={onPressItem}>
        <CoverView
          isVisible={!item.isAvailableForCurrentPlan}
          coverComponentSx={coverViewStyles}
          CoverComponent={() => (
            <React.Fragment>
              <CourseImageCoverBackground />
              <Icon
                name="lock-light"
                color="$background0"
                size={Platform.select({ default: 20, web: 30 })}
              />
            </React.Fragment>
          )}
        >
          <CourseImage
            source={{ uri: item.poster.normal?.url }}
            width={width}
            contentFit="cover"
          />
        </CoverView>
        <Separator size="$4" />
        <CourseInfoContainer>
          <Icon name="video-light" color="$text100" size={12} />
          <CourseInfoText>
            {t('common.courseItem.videoCount', {
              count: item.durationCourse?.videosCount,
            })}
          </CourseInfoText>
        </CourseInfoContainer>
        <Separator size="$4" />
        <Title numberOfLines={1}>{item.title}</Title>
        <Separator size="$2" />
        <Description
          numberOfLines={2}
          highlightChunks={
            extraStore?.getPositionById(item.id)?.description
          }
        >
          {item.description}
        </Description>
      </TouchableContainer>
    );
  },
);
